const HEADER_SELECTOR = '[data-rsv-architectural-header="true"]';
const CTA_SELECTOR = '[data-rsv-architectural-mobile-cta]';
const INITIALIZED_ATTRIBUTE = 'data-rsv-architectural-initialized';
const AUTO_TRANSPARENT_ATTRIBUTE = 'data-rsv-auto-transparent';
const AUTO_TRANSPARENT_CLASS_ATTRIBUTE = 'data-rsv-auto-transparent-class';
const FIRST_MAIN_SECTION_SELECTOR = '#MainContent > .shopify-section:first-child';
const HERO_MEDIA_SELECTOR = 'img, video';
const HERO_EDGE_TOLERANCE = 4;
const HERO_MIN_HEIGHT = 240;
const HERO_MIN_WIDTH_RATIO = 0.9;

function isVisible(element) {
  if (!(element instanceof HTMLElement)) return false;

  const style = window.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && Number.parseFloat(style.opacity) > 0;
}

function hasHeroGeometry(element, sectionRect) {
  if (!isVisible(element)) return false;

  const rect = element.getBoundingClientRect();
  const requiredWidth = Math.min(sectionRect.width, window.innerWidth) * HERO_MIN_WIDTH_RATIO;

  return (
    rect.width >= requiredWidth &&
    rect.height >= HERO_MIN_HEIGHT &&
    rect.top <= sectionRect.top + HERO_EDGE_TOLERANCE &&
    rect.bottom > sectionRect.top + HERO_MIN_HEIGHT
  );
}

function firstSectionHasAbuttingHero() {
  const firstSection = document.querySelector(FIRST_MAIN_SECTION_SELECTOR);
  if (!(firstSection instanceof HTMLElement)) return false;

  const sectionRect = firstSection.getBoundingClientRect();
  const media = Array.from(firstSection.querySelectorAll(HERO_MEDIA_SELECTOR));
  if (media.some((element) => hasHeroGeometry(element, sectionRect))) return true;

  return Array.from(firstSection.querySelectorAll('*')).some((element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (!window.getComputedStyle(element).backgroundImage.includes('url(')) return false;
    return hasHeroGeometry(element, sectionRect);
  });
}

function clearAutoTransparentState(header) {
  if (header.getAttribute(AUTO_TRANSPARENT_ATTRIBUTE) !== 'true') return;

  const autoClass = header.getAttribute(AUTO_TRANSPARENT_CLASS_ATTRIBUTE);
  header.removeAttribute('transparent');
  header.removeAttribute(AUTO_TRANSPARENT_ATTRIBUTE);
  header.removeAttribute(AUTO_TRANSPARENT_CLASS_ATTRIBUTE);

  if (autoClass) header.classList.remove(autoClass);
}

function updateAutoTransparentState(header) {
  const wasAutoTransparent = header.getAttribute(AUTO_TRANSPARENT_ATTRIBUTE) === 'true';

  if (wasAutoTransparent) clearAutoTransparentState(header);
  if (header.hasAttribute('transparent') || !firstSectionHasAbuttingHero()) return;

  const transparentColorClass = header.dataset.rsvTransparentColorClass;
  const transparentMode = header.hasAttribute('sticky') ? 'not-sticky' : 'always';

  header.setAttribute('transparent', transparentMode);
  header.setAttribute(AUTO_TRANSPARENT_ATTRIBUTE, 'true');

  if (transparentColorClass) {
    header.classList.add(transparentColorClass);
    header.setAttribute(AUTO_TRANSPARENT_CLASS_ATTRIBUTE, transparentColorClass);
  }
}

function initArchitecturalHeader(header) {
  if (!(header instanceof HTMLElement)) return;

  updateAutoTransparentState(header);
  if (header.hasAttribute(INITIALIZED_ATTRIBUTE)) {
    window.dispatchEvent(new Event('scroll'));
    return;
  }

  header.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
  const section = header.closest('.shopify-section');
  const mobileCta = section?.querySelector(CTA_SELECTOR);
  let frame = 0;

  const update = () => {
    frame = 0;
    const isScrolled = window.scrollY > 32;
    const isSolid = !header.hasAttribute('transparent') || isScrolled || header.dataset.stickyState === 'active';
    header.setAttribute('data-rsv-scrolled', isScrolled ? 'true' : 'false');
    header.setAttribute('data-rsv-solid', isSolid ? 'true' : 'false');

    if (mobileCta instanceof HTMLElement) {
      const headerRect = header.getBoundingClientRect();
      const nextTop = Math.max(0, Math.round(headerRect.bottom)) + 8;
      mobileCta.style.setProperty('--rsv-arch-mobile-cta-top', `${nextTop}px`);
    }
  };

  const scheduleUpdate = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate, { passive: true });

  const resizeObserver = new ResizeObserver(scheduleUpdate);
  resizeObserver.observe(header);

  update();
}

function init() {
  document.querySelectorAll(HEADER_SELECTOR).forEach(initArchitecturalHeader);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
